// src/store/index.js
import { createStore } from 'vuex'
import AuthService from '../services/authService'
import BusinessService from '../services/businessService'

export default createStore({
  state: {
    sidebarVisible: '',
    sidebarUnfoldable: false,
    user: AuthService.getCurrentUser(),
    businessRoleObject: null,
    businessStatus: null,
    isLoading: false,
  },
  mutations: {
    setAuthInitialized(state, status) {
      state.isAuthInitialized = status
    },
    toggleSidebar(state) {
      state.sidebarVisible = !state.sidebarVisible
    },
    toggleUnfoldable(state) {
      state.sidebarUnfoldable = !state.sidebarUnfoldable
    },
    updateSidebarVisible(state, payload) {
      state.sidebarVisible = payload.value
    },
    setLoading(state, status) {
      state.isLoading = status
    },
    setBusinessRoleObject(state, roleObject) {
      state.businessRoleObject = roleObject
    },
    setBusinessStatus(state, status) {
      state.businessStatus = status
    },
    setUser(state, user) {
      state.user = user
    },
    logout(state) {
      state.user = null
      state.businessRoleObject = null
      state.businessStatus = null
    },
  },
  actions: {
    async fetchBusinessStatus({ commit, getters }) {
      const businessId = getters.businessId
      const userRole = AuthService.getUserRole()
      if (!businessId) {
        return
      }
      if (!['owner', 'manager'].includes(userRole)) {
        return
      }
      commit('setLoading', true)
      try {
        const status = await BusinessService.getStatus(businessId)
        commit('setBusinessStatus', status)
      } catch (error) {
        commit('setBusinessStatus', null)
      } finally {
        commit('setLoading', false)
      }
    },
    async initializeAuth({ commit, dispatch }) {
      commit('setLoading', true)
      try {
        const user = AuthService.getCurrentUser()
        if (user) {
          commit('setUser', user)
          const roleObject = await AuthService.fetchPrimaryBusinessRole()
          commit('setBusinessRoleObject', roleObject)

          if (roleObject) {
            await dispatch('fetchBusinessStatus')
          }
        }
        commit('setAuthInitialized', true)
      } catch (error) {
        commit('logout')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },
    async login({ commit, dispatch, state }, userCredentials) {
      commit('setLoading', true)
      try {
        const loggedInUser = await AuthService.login(userCredentials)
        if (loggedInUser) {
          commit('setUser', loggedInUser)
          const roleObject = AuthService.businessRole
          commit('setBusinessRoleObject', roleObject)

          if (roleObject) {
            await dispatch('fetchBusinessStatus')
          }
        }
        return loggedInUser
      } catch (error) {
        commit('setLoading', false)
        throw error
      } finally {
        if (state.isLoading) {
          commit('setLoading', false)
        }
      }
    },

    // --- Google Login Action ---
    async googleLogin({ commit, dispatch, state }, payload) {
      commit('setLoading', true)
      try {
        // payload contains { token: '...', referral_code: '...' }
        const loggedInUser = await AuthService.googleLogin(payload)
        if (loggedInUser) {
          commit('setUser', loggedInUser)
          const roleObject = AuthService.businessRole
          commit('setBusinessRoleObject', roleObject)

          if (roleObject) {
            await dispatch('fetchBusinessStatus')
          }
        }
        return loggedInUser
      } catch (error) {
        commit('setLoading', false)
        throw error
      } finally {
        if (state.isLoading) {
          commit('setLoading', false)
        }
      }
    },

    logout({ commit }) {
      AuthService.logout()
      commit('logout')
    },
    async register(_, user) {
      await AuthService.register(user)
    },
    async completeSetup({ commit, dispatch, getters }) {
      const businessId = getters.businessId
      if (!businessId) return

      commit('setLoading', true)
      try {
        await BusinessService.completeSetup(businessId)
        await dispatch('fetchBusinessStatus')
      } catch (error) {
        console.error('Failed to complete setup:', error)
      } finally {
        commit('setLoading', false)
      }
    },
  },
  getters: {
    isAuthenticated: (state) => !!state.user,
    user: (state) => state.user,
    userRole: (state) => state.businessRoleObject?.role || null,
    businessId: (state) => state.businessRoleObject?.business || null,
    currentUserId: (state) => state.user?.public_id || null,
    isLoading: (state) => state.isLoading,
    businessStatus: (state) => state.businessStatus,

    hasLocation: (state) => {
      return state.businessStatus && state.businessStatus.location_count > 0
    },
    hasService: (state) => {
      return state.businessStatus && state.businessStatus.service_count > 0
    },
    hasSchedule: (state) => {
      return (
        state.businessStatus && !state.businessStatus.has_unassigned_schedule
      )
    },
    isAssignedService: (state) => {
      return (
        state.businessStatus && !state.businessStatus.has_unassigned_services
      )
    },
    isAssignedEmployee: (state) => {
      return (
        state.businessStatus && !state.businessStatus.has_unassigned_employees
      )
    },
    isInSetupMode: (state) => {
      if (!state.businessStatus) {
        return false
      }
      return state.businessStatus.is_in_setup_mode
    },
  },
})
